import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, Select, Tabs } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { dateTime, relativeTime } from '@/lib/format';
import { AddProspectModal } from '@/components/prospecting/AddProspect';
import { getProspectSetup, listProspects } from '@/data/client';
import type { Prospect, ProspectStatus } from '@/data/types';
import { useMe } from '@/auth/useMe';
import { useSearch } from '@/components/shell/search';
import { FindPanel } from '@/components/prospecting/FindPanel';
import { ProspectRuns } from '@/components/prospecting/ProspectRuns';
import { ProspectSetupPanel } from '@/components/prospecting/ProspectSetup';
import { attemptsLabel, ProspectStatusBadge } from '@/components/prospecting/ProspectBits';

type Tab = 'queue' | 'all' | 'find' | 'runs' | 'setup';

const STATUS_FILTER: { value: ProspectStatus | ''; label: string }[] = [
  { value: '', label: 'Every status' }, { value: 'new', label: 'Not called yet' }, { value: 'active', label: 'In rotation' },
  { value: 'cold', label: 'Cold' }, { value: 'closed', label: 'Closed' }, { value: 'converted', label: 'Became a lead' },
];

/* Cold-call prospecting (docs/prospecting-plan.md). Every member works the call
   queue; sales managers and admins also search, see the runs and edit setup. */
export function Prospecting() {
  const { me } = useMe();
  const [params, setParams] = useSearchParams();
  const manager = !!me?.isManager;
  const tabs: { value: Tab; label: string }[] = [
    { value: 'queue', label: 'Call queue' }, { value: 'all', label: 'Prospects' },
    ...(manager ? [{ value: 'find' as Tab, label: 'Find' }, { value: 'runs' as Tab, label: 'Searches' }, { value: 'setup' as Tab, label: 'Setup' }] : []),
  ];
  const tab = tabs.find((t) => t.value === params.get('tab'))?.value ?? 'queue';
  const go = (v: Tab) => setParams({ tab: v }, { replace: true });
  return (
    <div className="crm-list">
      <div className="lead-tabsbar"><Tabs items={tabs} value={tab} onChange={(v) => go(v as Tab)} /></div>
      {(tab === 'queue' || tab === 'all') && <ProspectList view={tab} />}
      {tab === 'find' && <FindPanel onStarted={() => go('runs')} />}
      {tab === 'runs' && <ProspectRuns />}
      {tab === 'setup' && <ProspectSetupPanel />}
    </div>
  );
}

function ProspectList({ view }: { view: 'queue' | 'all' }) {
  const navigate = useNavigate();
  const { query } = useSearch();
  const { me } = useMe();
  const [owner, setOwner] = useState(view === 'queue' ? 'mine_or_unassigned' : 'all');
  const [status, setStatus] = useState<ProspectStatus | ''>('');
  const [category, setCategory] = useState('');
  const [adding, setAdding] = useState(false);
  const { data: setup } = useQuery({ queryKey: ['prospectSetup'], queryFn: getProspectSetup, enabled: !!me?.isManager, staleTime: 5 * 60_000 });
  const { data, isLoading } = useQuery({
    queryKey: ['prospects', { view, owner, status, category, q: query }],
    queryFn: () => listProspects({ view, owner, status: view === 'all' ? status : '', category, q: query }),
    placeholderData: (prev) => prev,
  });
  // Under the queue: what is scheduled but not due yet, soonest first, so a rep sees the week ahead.
  const upcoming = useQuery({
    queryKey: ['prospects', { view: 'upcoming', owner, category, q: query }],
    queryFn: () => listProspects({ view: 'upcoming', owner, category, q: query }),
    enabled: view === 'queue',
    placeholderData: (prev) => prev,
  });
  const ownerOptions = [
    ...(view === 'queue' ? [{ value: 'mine_or_unassigned', label: 'Mine + unassigned' }] : []),
    { value: 'me', label: 'Mine' }, { value: 'unassigned', label: 'Unassigned' }, { value: 'all', label: 'Everyone' },
  ];
  const categories = [...new Set([...(setup?.categories.map((c) => c.name) ?? []), ...(data ?? []).map((p) => p.category).filter(Boolean) as string[]])];

  return (
    <>
      <div className="lead-toolbar">
        <div className="lead-toolbar__filters">
          <label className="lead-field"><Icon name="users" size={16} />
            <Select aria-label="Owner" options={ownerOptions} value={owner} onChange={(e) => setOwner(e.target.value)} /></label>
          {view === 'all' && (
            <label className="lead-field"><Icon name="filter" size={16} />
              <Select aria-label="Status" options={STATUS_FILTER} value={status} onChange={(e) => setStatus(e.target.value as ProspectStatus | '')} /></label>
          )}
          {categories.length > 1 && (
            <label className="lead-field">
              <Select aria-label="Category" options={[{ value: '', label: 'Every category' }, ...categories.map((c) => ({ value: c, label: c }))]}
                value={category} onChange={(e) => setCategory(e.target.value)} /></label>
          )}
        </div>
        <div className="lead-toolbar__right">
          <Button size="sm" variant="outline" iconLeft={<Icon name="plus" size={14} />} onClick={() => setAdding(true)}>Add prospect</Button>
        </div>
      </div>
      <Card padding={false}>
        <ProspectTable rows={data} onOpen={(id) => navigate(`/prospecting/${id}`)} />
        {!isLoading && !data?.length && (
          <p className="lead-none">{view === 'queue' ? 'No calls due. Add a prospect, or wait for the next attempts below to come up.' : 'No prospects match.'}</p>
        )}
      </Card>
      {view === 'queue' && (
        <Card padding={false} className="crm-section">
          <div className="crm-section__head"><h3>Coming up</h3><span className="crm-cell-sub">Scheduled attempts and call-backs, soonest first</span></div>
          <ProspectTable rows={upcoming.data} onOpen={(id) => navigate(`/prospecting/${id}`)} />
          {!upcoming.isLoading && !upcoming.data?.length && <p className="lead-none">Nothing scheduled. Logging a call sets the next attempt.</p>}
        </Card>
      )}
      {adding && <AddProspectModal onClose={() => setAdding(false)} />}
    </>
  );
}

function ProspectTable({ rows, onOpen }: { rows: Prospect[] | undefined; onOpen: (id: string) => void }) {
  return (
    <table className="dash-table lead-table pq-table">
      <thead><tr><th className="crm-num">Score</th><th>Business</th><th>Phone</th><th>Why call</th><th>Status</th><th>Next</th><th>Owner</th></tr></thead>
      <tbody>
        {rows?.map((p) => (
          <tr key={p.id} className={p.status === 'new' ? 'crm-row--new' : ''} onClick={() => onOpen(p.id)}>
            <td className="crm-num"><span className={`pq-score pq-score--${scoreBand(p.score)}`}>{p.score ?? '—'}</span></td>
            <td>
              <strong>{p.name}</strong>
              <span className="crm-cell-sub">{[p.category, [p.city, p.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}</span>
              {p.crmNote && <span className="crm-cell-sub pq-flag">{p.crmNote}</span>}
            </td>
            <td className="crm-cell-muted pq-nowrap">{p.phone ?? '—'}</td>
            <td className="crm-cell-muted pq-hook">{p.salesHook ?? (p.origin === 'manual' ? 'Added by hand' : '—')}</td>
            <td><StatusCell p={p} /></td>
            <td className="pq-nowrap"><NextCell p={p} /></td>
            <td className="crm-cell-muted">{p.ownerName ?? 'Unassigned'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* When the next touch is: "Due now" once the time has passed (or never set),
   otherwise the day and time, with "in 3 days" beneath. Nothing for archived. */
export function NextCell({ p }: { p: Pick<Prospect, 'status' | 'nextAttemptAt'> }) {
  if (p.status !== 'new' && p.status !== 'active') return <span className="crm-cell-muted">—</span>;
  if (!p.nextAttemptAt || Date.parse(p.nextAttemptAt) <= Date.now()) return <span className="pq-due">Due now</span>;
  return <><span>{dateTime(p.nextAttemptAt)}</span><span className="crm-cell-sub">{relativeTime(p.nextAttemptAt)}</span></>;
}

function StatusCell({ p }: { p: Prospect }) {
  // Reached a person last time (call back or a reply): the next touch is a follow-up, not a cold attempt.
  const reached = p.status === 'active' && (p.lastOutcome === 'callback' || p.lastOutcome === 'replied');
  return (
    <div className="pq-status">
      <span>
        {reached ? <Badge variant="accent">{p.lastOutcome === 'replied' ? 'Follow up' : 'Call back'}</Badge> : <ProspectStatusBadge p={p} />}
        {p.timesFound > 1 && <Badge variant="outline">Found {p.timesFound}×</Badge>}
      </span>
      <span className="crm-cell-sub">
        {p.callCount ? attemptsLabel(p.callCount) : 'Not contacted'}
        {p.lastCallAt ? ` · last ${relativeTime(p.lastCallAt)}` : ''}
      </span>
    </div>
  );
}

export const scoreBand = (s: number | null) => (s == null ? 'none' : s >= 70 ? 'high' : s >= 50 ? 'mid' : 'low');
